import { Injectable } from '@angular/core';
import { AccountTypesFull, InternalTransferPayload, ISubmit } from '@interfaces';
import { FlowTypes, MultiSignerSignState } from '@interfaces/models';
import {
  isMultiPostPayload,
  MultiSignerPostPayload,
  MXMPatchResponse,
} from '@interfaces/models/multi-signer-approval';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { sanitizeError } from '@shared/helpers/sanitize-error-helper';
import { AccountTypes, flowMapper } from '@shared/models';
import { LoggerCodes } from '@shared/modules/logger/logger';
import { FMFService } from '@shared/services';
import { MultiSignerApprovalService } from '@shared/services/multi-signer-approval/multi-signer-approval.service';
import {
  MXMAppianSubmit,
  MXMApproval,
  MXMApprovalCompleted,
  MXMApprovalFailed,
  MXMApprovalInProgress,
  MXMFinalCompleted,
  MXMFinalSign,
  MXMITSubmit,
  MXMSubmit,
  UpdateMXMSignState,
  UpdateMXMTransferId,
} from '@store/actions/mxm/mxm.actions';
import { selectMultiITCO0Payloads } from '@store/internal-transfers/selectors/submit/multi-it-submit.selectors';
import { MXMApprovalStatus } from '@store/reducers/mxm/mxm.reducer';
import { selectClientAccountsEntity } from '@store/selectors/client-accounts/client-accounts.selectors';
import {
  getMultiSignerPostPayload,
  selectIsMultiITMxm,
  selectMXMApprovalStatus,
  selectMXMTransferId,
} from '@store/selectors/mxm/mxm.selectors';
import { getITSubmitPayload, getSubmitPayload } from '@store/selectors/submit/submit.selectors';
import { selectTriageState } from '@store/selectors/triage/triage.selectors';
import { catchError, filter, forkJoin, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { selectRecaptchaMockResult } from 'vg-interaction-model';

import * as fromRootStore from '../../index';
import {
  MXMConvertHoldings,
  selectInternalTransferConvertAvgCostHoldings,
  selectInternalTransferConvertSpecIDHoldings,
  submitMetricsFlowSubmit,
  TrackProcessComplete,
  UpdateIsFinalSigner,
  updateIsStp,
} from '../../index';
import { Go, LogError, Next, TrackProcessError } from '../../index';

type ActionList = (
  | ({
      payload: MultiSignerSignState;
    } & Action<'[Transfer Review] Update Sign State for Additional Signer'>)
  | Action<'[Request] MXM Submit for Final Signer'>
  | Action<'[Request] MXM Approval Completed'>
  | Action<'[Submit] MXM Dispatch Correct Submission Flow for Final Signer'>
)[];
@Injectable()
export class MXMEffects {
  constructor(
    private actions$: Actions,
    private mxmService: MultiSignerApprovalService,
    private store$: Store<fromRootStore.State>,
    private fmfService: FMFService,
  ) {}

  mxmApproval$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MXMApproval),
      withLatestFrom(
        this.store$.pipe(select(selectMXMApprovalStatus)),
        this.store$.pipe(select(getMultiSignerPostPayload)),
        this.store$.pipe(select(selectClientAccountsEntity)),
        this.store$.pipe(select(selectMXMTransferId)),
      ),
      // multiSignerApprovalStatus is set to PENDING when approval process is in progress (to prevent multiple approval requests from being sent)
      filter(
        ([, multiSignerApprovalStatus]) => multiSignerApprovalStatus !== MXMApprovalStatus.PENDING,
      ),
      tap(() => {
        this.store$.dispatch(MXMApprovalInProgress());
      }),
      switchMap(([, , MultiSignerPostPayload, clientAccountsEntity, transferId]) => {
        return transferId
          ? this.additionalSigner(transferId, MultiSignerPostPayload.filenet)
          : this.initiator(MultiSignerPostPayload, clientAccountsEntity.vgClientPoid);
      }),
    ),
  );

  private initiator(mxmPayload: MultiSignerPostPayload, poid: string) {
    const eventId = isMultiPostPayload(mxmPayload)
      ? 'error-page-catch-multiSignerApprovalService-sendTransfer-multi-it'
      : 'error-page-catch-multiSignerApprovalService-sendTransfer';
    return this.mxmService.sendTransfer(mxmPayload, poid).pipe(
      switchMap(() => {
        const actions: ActionList = [
          UpdateMXMSignState({ payload: MultiSignerSignState.ACCEPT }),
          MXMApprovalCompleted(),
        ];
        return actions;
      }),
      catchError((e) => {
        return [
          MXMApprovalFailed(),
          fromRootStore.LogError({
            payload: {
              eventId,
              message: `MXM Approval Service Error: ${JSON.stringify(sanitizeError(e))}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        ];
      }),
    );
  }

  private additionalSigner(
    transferId: string,
    filenetPayload: ISubmit | InternalTransferPayload[],
  ) {
    return this.mxmService.addOwnerConsentForMXM(transferId, filenetPayload).pipe(
      switchMap((result: MXMPatchResponse) => {
        const actions: [Action] = [UpdateMXMSignState({ payload: MultiSignerSignState.ACCEPT })];
        if (!result.allSigned) {
          return [...actions, MXMApprovalCompleted()];
        }
        //If all signed, kick off Final Signature Process
        return [...actions, MXMFinalSign()];
      }),
      catchError((e) => {
        return [
          MXMApprovalFailed(),
          LogError({
            payload: {
              eventId: 'error-page-catch-MXMingApprovalService-addOwnerConsentForMXM',
              message: `MXM Approval Service Error: ${JSON.stringify(sanitizeError(e))}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        ];
      }),
    );
  }

  mxmSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MXMSubmit),
      map((action) => {
        return action.payload;
      }),
      switchMap(({ recaptchaResult, fmfResponse, submitPayload, transferId }) => {
        return this.mxmService
          .finalizeTransfer(recaptchaResult, fmfResponse, submitPayload, transferId)
          .pipe(
            switchMap((res) => {
              let flowType: FlowTypes;
              let accountType: AccountTypes | AccountTypesFull | undefined;
              let transactionId: string;

              if (Array.isArray(submitPayload)) {
                // Multi-IT: the payload is an array of CO0 transfer payloads; the from
                // account (and its type) is shared across every payload, so read it off
                // the first element.
                flowType = FlowTypes.INTERNAL_TRANSFER;
                accountType = submitPayload[0].fromAccount.accountType;
              } else if ('config' in submitPayload) {
                flowType = submitPayload.config.triage.flowType;
                accountType = submitPayload.config.triage.accountTypeInheriting;
              } else {
                flowType = FlowTypes.INTERNAL_TRANSFER;
                accountType = submitPayload.itConfig.internalTransfer.triage.transferFrom;
              }
              const isTransactIDTypeSTP = 'transactId' in res;
              if ('transactId' in res) {
                transactionId = res.transactId;
              } else {
                transactionId = res.appianSuccess?.bpmCaseId?.toString().replace(/,/g, '|');
              }
              return [
                TrackProcessComplete({
                  processType: `coo-${flowMapper[flowType]}-flow`,
                  accountType: accountType,
                  transactionId,
                }),
                updateIsStp({ payload: isTransactIDTypeSTP }),
                MXMFinalCompleted({ payload: transactionId }),
              ];
            }),
            catchError((e) => {
              return [
                MXMApprovalFailed(),
                fromRootStore.LogError({
                  payload: {
                    eventId: 'error-page-catch-multiSignerApprovalService-finalizeTransfer',
                    message: `MXM Approval Service Error: ${JSON.stringify(sanitizeError(e))}`,
                    code: LoggerCodes.NULL_FAILURE,
                  },
                }),
              ];
            }),
          );
      }),
    ),
  );
  mxmAppianSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MXMAppianSubmit),
      withLatestFrom(
        this.store$.pipe(select(selectRecaptchaMockResult)),
        this.store$.pipe(select(getSubmitPayload)),
        this.store$.pipe(select(selectMXMTransferId)),
      ),
      switchMap(([, recaptchaLevel, submitPayload, transferId]) => {
        return forkJoin([
          of(recaptchaLevel),
          this.fmfService.getAllTokens({ recaptcha: 'COO_OA_SUBMIT' }),
          of(submitPayload),
          of(transferId),
        ]);
      }),
      switchMap(([recaptchaResult, fmfResponse, submitPayload, transferId]) => {
        return of(
          MXMSubmit({ payload: { recaptchaResult, fmfResponse, submitPayload, transferId } }),
        );
      }),
    ),
  );

  // Both single- and multi-IT final signers submit through this effect. Multi-IT sends the
  // CO0 payloads array (selectMultiITCO0Payloads); single-IT sends the itConfig payload. Both
  // route through the shared MXMSubmit -> mxmSubmit$ -> finalizeTransfer path. recaptcha/fmf
  // are gathered for both; finalizeTransfer omits them from the multi-IT body per the contract.
  mxmItSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MXMITSubmit),
      withLatestFrom(
        this.store$.pipe(select(selectRecaptchaMockResult)),
        this.store$.pipe(select(getITSubmitPayload)),
        this.store$.pipe(select(selectMultiITCO0Payloads)),
        this.store$.pipe(select(selectIsMultiITMxm)),
        this.store$.pipe(select(selectMXMTransferId)),
      ),
      switchMap(([, recaptchaLevel, itPayload, multiItPayload, isMultiITMxm, transferId]) => {
        return forkJoin([
          of(recaptchaLevel),
          this.fmfService.getAllTokens({ recaptcha: 'COO_OA_SUBMIT' }),
          of(isMultiITMxm ? multiItPayload : itPayload),
          of(transferId),
        ]);
      }),
      switchMap(([recaptchaResult, fmfResponse, submitPayload, transferId]) => {
        return of(
          MXMSubmit({ payload: { recaptchaResult, fmfResponse, submitPayload, transferId } }),
        );
      }),
    ),
  );

  mxmFinalSigner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MXMFinalSign),
      withLatestFrom(
        this.store$.pipe(select(selectTriageState)),
        this.store$.pipe(select(selectInternalTransferConvertAvgCostHoldings)),
        this.store$.pipe(select(selectInternalTransferConvertSpecIDHoldings)),
        this.store$.pipe(select(selectIsMultiITMxm)),
      ),
      switchMap(([, triageState, itConvertAvgCost, itConvertSpecId, isMultiITMxm]) => {
        this.store$.dispatch(UpdateIsFinalSigner({ payload: true }));

        if (triageState.flowType == FlowTypes.GIFTING) {
          return of(MXMAppianSubmit());
        }
        //Multi-IT final signer submits through the shared MXMITSubmit -> finalizeTransfer
        //path (mxmItSubmit$ picks the multi-IT payload when isMultiITMxm), matching the
        //single-IT final-signer path so the submission is tied to the existing transferId;
        //the backend fans out the individual transactions. Cost-basis conversion is carried
        //in the CO0 payload per-holding (costBasisMethod), so we skip the MXMConvertHoldings
        //detour used by the single-IT path.
        if (isMultiITMxm) {
          return of(MXMITSubmit());
        }
        //If we have to convert, this will do the conversion, then call MXMITSubmit
        if (itConvertAvgCost || itConvertSpecId) {
          return of(MXMConvertHoldings());
        }
        //If we do not have to convert, then we can simply call MXMITSubmit
        return of(MXMITSubmit());
      }),
    ),
  );

  mxmApprovalCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MXMApprovalCompleted),
      withLatestFrom(this.store$.select(selectMXMTransferId)),
      switchMap(([, transferId]) => [
        Next(),
        UpdateMXMTransferId({ payload: transferId || '' }),
        submitMetricsFlowSubmit({ payload: '' }),
      ]),
    ),
  );

  mxmFinalCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MXMFinalCompleted),
      withLatestFrom(this.store$.select(selectMXMTransferId)),
      switchMap(([action, transferId]) => [
        Next(),
        UpdateMXMTransferId({ payload: transferId || '' }),
        submitMetricsFlowSubmit({ payload: action.payload }),
      ]),
    ),
  );

  private initiatorError() {
    return [
      Go({ path: ['error'] }),
      LogError({
        payload: {
          eventId: 'error-page-route-mxm-approval-initiator',
          message: 'Failed to initiate mxm Approval send transfer',
          code: LoggerCodes.NULL_FAILURE,
        },
      }),
      TrackProcessError({
        errorCode: 'coo_mxm_approval_error-initiator',
      }),
    ];
  }

  private additionalSignerError() {
    return [
      Go({ path: ['error'] }),
      LogError({
        payload: {
          eventId: 'error-page-route-mxm-approval-additional-signer',
          message: 'Failed to initiate mxm Approval additional signing',
          code: LoggerCodes.NULL_FAILURE,
        },
      }),
      TrackProcessError({
        errorCode: 'coo_mxm_approval_error-additional-signer',
      }),
    ];
  }

  mxmApprovalFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MXMApprovalFailed),
      withLatestFrom(this.store$.pipe(select(selectMXMTransferId))),
      switchMap(([, transferId]) => {
        return transferId ? this.additionalSignerError() : this.initiatorError();
      }),
    ),
  );
}
